/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {updateSSOConfiguration} from '@ion/core/src/services/Connect';
import {SSOConfiguration} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseUpdateSSOConfigurationResult = UseDefaultQueryResult & {
  update: (ssoConfig: SSOConfiguration) => void;
};

const updateSSO = async(ssoConfig: SSOConfiguration) => {
  try {
    const updated = await updateSSOConfiguration(ssoConfig);
    return updated;
  } catch (err) {
    error(err);
    throw new Error(err);
  }
};

/**
 * Common hook for editing a SSO Configuration entity.
 *
 * ```typescript
 *
 *    import useUpdateSSOConfiguration from '@ion/query/src/hooks/';
 *
 *    const {save, isSuccess, isError} = useUpdateSSOConfiguration();
 *
 * ```
 */
export const useUpdateSSOConfiguration = (): UseUpdateSSOConfigurationResult => {
  const queryClient = useQueryClient();

  const mutation    = useMutation(updateSSO, {
    onSettled : () => {
      queryClient.invalidateQueries([QueryKey.UPDATE_SSO_CONFIGURATION]);
    }
  });

  const update = useCallback((ssoConfig: SSOConfiguration) => {
    mutation.mutate(ssoConfig);
  }, [mutation]);

  return createMutationResult(
    mutation, {update}
  ) as UseUpdateSSOConfigurationResult;
};

export default useUpdateSSOConfiguration;
